// The sketch as efmodel JSON, editable. Valid edits update the sketch (and the diagram) after a
// short pause; problems are marked where they are, with the validator's hints.
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { json } from '@codemirror/lang-json';
import { bracketMatching, syntaxHighlighting } from '@codemirror/language';
import { forceLinting, linter, lintGutter, type Diagnostic } from '@codemirror/lint';
import { highlightSelectionMatches, search, searchKeymap } from '@codemirror/search';
import { EditorState } from '@codemirror/state';
import { drawSelection, EditorView, highlightActiveLine, keymap, lineNumbers } from '@codemirror/view';
import { parseDocument, serializeDocument, type EfModelDocument } from '@modelviewer/core';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { editorTheme, highlight } from '../lib/editorTheme';
import { jsonPaths, rangeOf } from '../lib/jsonPaths';
import { setSketchDoc } from '../state/sketches';

const APPLY_DELAY = 600;

function diagnose(text: string): { diagnostics: Diagnostic[]; doc: EfModelDocument | null } {
  try {
    JSON.parse(text);
  } catch (e) {
    const match = /position (\d+)/.exec((e as Error).message);
    const at = match ? Math.min(Number(match[1]), text.length) : 0;
    return { diagnostics: [{ from: at, to: Math.min(at + 1, text.length), severity: 'error', message: (e as Error).message }], doc: null };
  }
  const result = parseDocument(text);
  const paths = jsonPaths(text);
  const diagnostics = result.issues.map((issue): Diagnostic => {
    const range = rangeOf(paths, issue.path) ?? { from: 0, to: 0 };
    return {
      from: range.from,
      to: range.to,
      severity: issue.severity === 'error' ? 'error' : 'warning',
      source: issue.code,
      message: issue.hint ? `${issue.message}\n${issue.hint}` : issue.message,
    };
  });
  const ok = !result.issues.some((i) => i.severity === 'error');
  return { diagnostics, doc: ok ? result.document : null };
}

export function SketchJson({ sketchId, doc }: { sketchId: string; doc: EfModelDocument }) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  /** The document the editor text last came from or produced, so outside changes can be told apart. */
  const shown = useRef<EfModelDocument | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const [status, setStatus] = useState<{ errors: number; warnings: number; pending: boolean }>({ errors: 0, warnings: 0, pending: false });

  useEffect(() => {
    const apply = (text: string) => {
      const { diagnostics, doc: next } = diagnose(text);
      setStatus({ errors: diagnostics.filter((d) => d.severity === 'error').length, warnings: diagnostics.filter((d) => d.severity === 'warning').length, pending: false });
      if (next) {
        shown.current = next;
        setSketchDoc(sketchId, next);
      }
    };
    view.current = new EditorView({
      parent: host.current!,
      state: EditorState.create({
        doc: '',
        extensions: [
          lineNumbers(),
          history(),
          drawSelection(),
          highlightActiveLine(),
          bracketMatching(),
          highlightSelectionMatches(),
          search({ top: true }),
          json(),
          lintGutter(),
          linter((v) => diagnose(v.state.doc.toString()).diagnostics, { delay: 300 }),
          keymap.of([...defaultKeymap, ...historyKeymap, ...searchKeymap]),
          syntaxHighlighting(highlight),
          editorTheme,
          EditorView.contentAttributes.of({ 'aria-label': 'Sketch JSON' }),
          EditorView.updateListener.of((update) => {
            if (!update.docChanged || !update.transactions.some((t) => t.isUserEvent('input') || t.isUserEvent('delete') || t.isUserEvent('undo') || t.isUserEvent('redo') || t.isUserEvent('move'))) return;
            setStatus((s) => ({ ...s, pending: true }));
            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => apply(update.state.doc.toString()), APPLY_DELAY);
          }),
        ],
      }),
    });
    return () => {
      window.clearTimeout(timer.current);
      view.current?.destroy();
      view.current = null;
    };
  }, [sketchId]);

  // Show changes made elsewhere (the diagram, the inspector, undo).
  useEffect(() => {
    const v = view.current;
    if (!v || shown.current === doc) return;
    shown.current = doc;
    window.clearTimeout(timer.current);
    const text = serializeDocument(doc);
    if (v.state.doc.toString() !== text) v.dispatch({ changes: { from: 0, to: v.state.doc.length, insert: text } });
    const { diagnostics } = diagnose(text);
    setStatus({ errors: diagnostics.filter((d) => d.severity === 'error').length, warnings: diagnostics.filter((d) => d.severity === 'warning').length, pending: false });
    forceLinting(v);
  }, [doc]);

  return (
    <div className="flex h-full min-w-0 flex-col" data-testid="sketch-json">
      <div className="flex h-8 flex-none items-center gap-2 border-b border-border px-3 text-[12px]">
        <span className="font-medium">JSON</span>
        <span className="text-faint">efmodel 1.0</span>
        <span className="ml-auto flex items-center gap-1.5">
          {status.pending ? (
            <span className="text-faint">Editing…</span>
          ) : status.errors ? (
            <span className="flex items-center gap-1 text-removed">
              <CircleAlert size={13} /> {status.errors} error{status.errors > 1 ? 's' : ''}, not applied
            </span>
          ) : (
            <span className="flex items-center gap-1 text-added">
              <CircleCheck size={13} /> Valid{status.warnings ? `, ${status.warnings} warning${status.warnings > 1 ? 's' : ''}` : ''}
            </span>
          )}
        </span>
      </div>
      <div ref={host} className="min-h-0 flex-1 overflow-hidden [&_.cm-editor]:h-full" />
    </div>
  );
}
